/**
 * Saved combos — the favourites library behind F-CB-28.
 *
 * Two rules, and both of them are about *identity of movement* rather than
 * identity of record:
 *
 *  - **Save removes any entry with the same `sequenceKey` and inserts at index
 *    0.** `comboSequenceKey` deliberately excludes timestamps, ids, locks,
 *    brackets and branches, so re-saving a structurally identical score updates
 *    the stored copy in place and moves it to the front instead of leaving the
 *    user with two favourites that call the same punches.
 *  - **Load prunes duplicates keeping the first, and re-persists the pruned
 *    list.** Duplicates that predate the rule are removed the first time the
 *    library is read, so the pruning is a repair rather than a filter the read
 *    has to keep re-applying.
 *
 * Persist failures are silent (F-CB-28). Not "ignored": the read still returns
 * the pruned list, so what the user sees is correct either way, and the repair
 * is simply retried the next time the library is read. Interrupting a session
 * to report that a housekeeping delete did not land would be a worse answer
 * than trying again.
 *
 * Order is persisted the same way the saved-workout library persists it — an
 * explicit `position` beside each record — because "insert at index 0" is a
 * statement about an order, and a record store has none of its own.
 */

import type {
  BoxingStance,
  ComboId,
  ComboScore,
  Milliseconds,
  PersistedComboScore,
  SchemaVersion,
  StoreReadResult,
  WriteOutcome,
} from "@ff/domain";
import {
  DEFAULT_COMBO_PARAMETERS,
  STORE_IDS,
  comboId,
  comboSequenceKey,
  persistComboScore,
  schemaVersion,
} from "@ff/domain";
import type {
  KeyedRecord,
  ObservedMutationGate,
  RecordKey,
  RecordMutation,
  RecordStorePort,
} from "@ff/domain/ports";

import type { PayloadDecode, RecordDatabase, RecordIndexFields } from "../kernel";
import { createRecordStore } from "../kernel";

import { hydratePersistedComboScore } from "./hydrate";

/**
 * The version this build stamps on a saved-combo envelope.
 *
 * `savedCombos` has `currentSchemaVersion: null` in `STORE_DESCRIPTORS`:
 * `PersistedComboScore` carries no payload version and treats every field as
 * optional, so the envelope is the only thing that can be versioned. A record
 * above this version is refused by the codec, which freezes every mutation on
 * the store rather than letting this build write over a newer build's work.
 */
export const SAVED_COMBO_ENVELOPE_VERSION: SchemaVersion = schemaVersion(1);

/** One row in the favourites library: a stored score and where it sits. */
export interface SavedComboRecord {
  readonly position: number;
  readonly score: PersistedComboScore;
}

/**
 * The favourites library.
 *
 * `@ff/domain` declares no port for this store, so the interface is declared
 * beside its only implementation. It is shaped like the other storage ports on
 * purpose — a tri-state read, an observable gate, verified writes — because a
 * favourites list that answered `T | undefined` would let a library the app
 * could not decode be presented as a library the user never filled, and Build
 * can select `saved` as a section's combo source, so a silently shortened list
 * changes what a guided round calls.
 */
export interface ComboFavoritesStorePort {
  /**
   * Favourites in their saved order, newest-saved first. Duplicates by
   * `sequenceKey` are pruned here and the pruning is written back.
   */
  read(): Promise<StoreReadResult<readonly ComboScore[]>>;

  mutationGate(): ObservedMutationGate;

  /** Removes every entry with the same `sequenceKey` and inserts at index 0. */
  save(score: ComboScore): Promise<WriteOutcome>;

  remove(id: ComboId): Promise<WriteOutcome>;
}

/**
 * Any object decodes. `PersistedComboScore` treats every field as optional
 * precisely so a stored favourite always decodes, and a validator that demanded
 * more here would reject the old records the optionality exists to carry. What
 * is checked is the row this store wrote around it — the sort key — because a
 * row without one has no place in an order the user arranged.
 */
function decodeSavedComboRecord(payload: unknown): PayloadDecode<SavedComboRecord> {
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
    return { ok: false, trigger: "corruptBytes", detail: "saved combo row is not an object" };
  }
  const row = payload as Record<string, unknown>;

  const position = row["position"];
  if (typeof position !== "number" || !Number.isInteger(position)) {
    return {
      ok: false,
      trigger: "corruptBytes",
      detail: "saved combo row has no integer position",
    };
  }

  const score = row["score"];
  if (score === null || typeof score !== "object" || Array.isArray(score)) {
    return { ok: false, trigger: "corruptBytes", detail: "saved combo row has no score" };
  }

  return { ok: true, value: { position, score: score as PersistedComboScore } };
}

/** The materialized scalars the layout declares for `savedCombos`. */
function indexSavedCombo(record: SavedComboRecord): RecordIndexFields {
  const createdAt: Milliseconds = record.score.createdAt ?? 0;
  const stance: BoxingStance = record.score.stance ?? DEFAULT_COMBO_PARAMETERS.stance;
  return { createdAt, stance };
}

/** The `savedCombos` record store, configured with this store's codec and index. */
export function createComboRecordStore(
  database: RecordDatabase,
): RecordStorePort<SavedComboRecord> {
  return createRecordStore<SavedComboRecord>({
    store: STORE_IDS.savedCombos,
    schemaVersion: SAVED_COMBO_ENVELOPE_VERSION,
    database,
    decodePayload: decodeSavedComboRecord,
    index: indexSavedCombo,
    // Physical identity, not movement identity: two rows claiming one `ComboId`
    // make "the combo this section referenced" unanswerable and close the
    // library. A repeated `sequenceKey` is a different problem entirely — the
    // same movement saved twice — and is repaired on read.
    identity: (record) => record.value.score.id ?? record.key,
  });
}

/**
 * The favourites rules over any record store that holds saved-combo rows. Split
 * from {@link createComboRecordStore} for the same reason the saved-workout
 * library is: deduplication and ordering are independent of where the bytes
 * live.
 */
export function comboFavorites(
  records: RecordStorePort<SavedComboRecord>,
): ComboFavoritesStorePort {
  return new IndexedDbComboFavoritesStore(records);
}

export function createComboFavoritesStore(
  database: RecordDatabase,
): ComboFavoritesStorePort {
  return comboFavorites(createComboRecordStore(database));
}

class IndexedDbComboFavoritesStore implements ComboFavoritesStorePort {
  readonly #records: RecordStorePort<SavedComboRecord>;

  constructor(records: RecordStorePort<SavedComboRecord>) {
    this.#records = records;
  }

  async read(): Promise<StoreReadResult<readonly ComboScore[]>> {
    const result = await this.#records.read();
    if (result.status !== "loaded") return result;

    const ordered = [...result.value].sort(byPosition);
    const seenSequences = new Set<string>();
    const scores: ComboScore[] = [];
    const duplicates: RecordKey[] = [];

    for (const record of ordered) {
      const score = hydrate(record);
      const sequence = comboSequenceKey(score);
      if (seenSequences.has(sequence)) {
        duplicates.push(record.key);
        continue;
      }
      seenSequences.add(sequence);
      scores.push(score);
    }

    if (duplicates.length > 0) {
      // Re-persist the pruned list. The outcome is deliberately not consulted:
      // the value returned below is already the pruned one, and a failed
      // repair is simply repeated on the next read.
      await this.#records.commit(duplicates.map((key) => ({ kind: "remove", key })));
    }

    return { ...result, value: scores };
  }

  mutationGate(): ObservedMutationGate {
    return this.#records.mutationGate();
  }

  async save(score: ComboScore): Promise<WriteOutcome> {
    const current = await this.#records.read();
    if (current.status === "unreadable") {
      return { status: "refused", reason: current.trigger };
    }
    const records = current.status === "loaded" ? [...current.value].sort(byPosition) : [];
    const sequence = comboSequenceKey(score);

    const removals: RecordKey[] = [];
    const survivors: SavedComboRecord[] = [];
    for (const record of records) {
      // The row under this id is being overwritten by the put below. Emitting a
      // removal for it too would make the batch delete and re-create the same
      // key, and the removal's own verification — "this key must read back
      // absent" — would then fail against the row the put had just written.
      if (record.key === score.id) continue;
      if (comboSequenceKey(hydrate(record)) === sequence) {
        removals.push(record.key);
        continue;
      }
      survivors.push(record.value);
    }

    // Index 0 means "before every entry that survives", which is a position
    // rather than a renumbering. Shifting every other row up by one would turn
    // one save into a whole-library rewrite for no user-visible difference.
    let front = 0;
    for (const survivor of survivors) front = Math.min(front, survivor.position);
    front -= 1;

    const mutations: RecordMutation<SavedComboRecord>[] = [
      ...removals.map((key): RecordMutation<SavedComboRecord> => ({ kind: "remove", key })),
      {
        kind: "put",
        key: score.id,
        value: { position: front, score: persistComboScore(score) },
      },
    ];
    return this.#records.commit(mutations);
  }

  async remove(id: ComboId): Promise<WriteOutcome> {
    return this.#records.commit([{ kind: "remove", key: id }]);
  }
}

/**
 * The record key is the combo's identity, so it is what the score is rebuilt
 * under. A payload `id` that disagrees with its key is already a duplicate
 * identity the read refuses on; using the key here keeps hydration from
 * inventing a third answer.
 */
function hydrate(record: KeyedRecord<SavedComboRecord>): ComboScore {
  return hydratePersistedComboScore(comboId(record.key), record.value.score);
}

/**
 * Position first, then the record key. The tie-break can only matter for rows
 * this build did not write, and a favourites list whose order depended on
 * IndexedDB's iteration order would reshuffle itself for reasons nobody could
 * reproduce.
 */
function byPosition(
  left: KeyedRecord<SavedComboRecord>,
  right: KeyedRecord<SavedComboRecord>,
): number {
  if (left.value.position !== right.value.position) {
    return left.value.position - right.value.position;
  }
  if (left.key === right.key) return 0;
  return left.key < right.key ? -1 : 1;
}
